import { Component, ElementRef, Input, OnInit, ViewChild, Renderer2 } from '@angular/core';
import { PDFPageProxy, TextContent, TextItem } from 'pdfjs-dist/types/src/display/api';
import { PdfService } from './pdf.service';
import * as pdfjsLib from 'pdfjs-dist';

(pdfjsLib as any).GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${(pdfjsLib as any).version}/pdf.worker.min.mjs`;

@Component({
    selector: 'lex-pdf-viewer',
    templateUrl: './pdf-viewer.component.html',
    styleUrls: ['./pdf-viewer.component.scss'],
    standalone: false
})
export class PdfViewerComponent implements OnInit {
  @Input() pdfUrl: string;
  @ViewChild('pdfContainer', { static: true }) pdfContainer: ElementRef<HTMLDivElement>;
  @ViewChild('scrollContainer', { static: true }) scrollContainer: ElementRef<HTMLDivElement>;

  public currentPageNumber: number = 1;
  public pageCount: number = 1;
  public zoom: number = 110;

  private pdf: any;
  private scale = 1.1;
  private wordsToHighlight: string[] = ['Giannis', 'NBA', 'MVP', 'Bucks', 'basketball'];

  constructor(private pdfService: PdfService, private renderer: Renderer2) { }

  public ngOnInit(): void {
    this.loadPdf();
  }

  public goToPage(): void {
    if (this.currentPageNumber <= 0 || this.currentPageNumber > this.pageCount) {
      console.error('Invalid page number');
      return;
    }
    const pageElement = document.getElementById(`page-${this.currentPageNumber}`);
    if (pageElement) {
      pageElement.scrollIntoView({ behavior: 'smooth' });
    }
  }

  public zoomIn(): void {
    this.zoom += 10;
    this.scale = this.zoom / 100;
    this.renderAllPages();
  }

  public zoomOut(): void {
    if (this.zoom > 10) {
      this.zoom -= 10;
      this.scale = this.zoom / 100;
      this.renderAllPages();
    }
  }

  public updateZoom(): void {
    this.scale = this.zoom / 100;
    this.renderAllPages();
  }

  private highlightWords(textContent: TextContent, canvas: HTMLCanvasElement, viewport: any): void {
    const context = canvas.getContext('2d');

    if (!textContent) {
      console.error('Text content not loaded');
      return;
    }

    textContent.items.forEach((item: TextItem) => {
      const text = item.str;
      const transform = pdfjsLib.Util.transform(viewport.transform, item.transform);
      const x = transform[4];
      const y = transform[5];
      const fontSize = Math.abs(transform[3]);

      context.font = `${fontSize}px sans-serif`;
      const words = text.split(' ');

      let currentX = x;
      words.forEach((w) => {
        const width = context.measureText(w).width;
        if (this.wordsToHighlight.some(word => w.toLocaleLowerCase().includes(word.toLocaleLowerCase()))) {
          context.fillStyle = 'rgba(255, 255, 0, 0.5)';
          context.fillRect(currentX, y - fontSize, width, fontSize);
        }
        currentX += width + context.measureText(' ').width;
      });
    });
  }

  private loadPdf(): void {
    if (!this.pdfUrl) {
      return;
    }
    this.pdfService.loadPdf(this.pdfUrl).then(pdf => {
      this.pdf = pdf;
      this.pageCount = pdf.numPages;
      this.renderAllPages();
    }).catch(error => {
      console.error('Error loading PDF: ', error);
    });
  }

  private renderAllPages(): void {
    const pdfContainer = this.pdfContainer.nativeElement;
    pdfContainer.innerHTML = ''; // Clear the container

    for (let pageNum = 1; pageNum <= this.pdf.numPages; pageNum++) {
      this.pdf.getPage(pageNum).then((page: PDFPageProxy) => {
        const viewport = page.getViewport({ scale: this.scale });
        const canvas = document.createElement('canvas');
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        const pageDiv = document.createElement('div');
        pageDiv.id = `page-${pageNum}`;
        pageDiv.style.marginBottom = '20px';
        pageDiv.appendChild(canvas);
        pdfContainer.appendChild(pageDiv);

        const renderContext = {
          canvasContext: canvas.getContext('2d'),
          viewport: viewport
        };

        page.render(renderContext).promise.then(() => {
          page.getTextContent().then(textContent => {
            this.highlightWords(textContent, canvas, viewport);
          });
        }).catch(error => {
          console.error('Error rendering page: ', error);
        });
      }).catch(error => {
        console.error('Error getting page: ', error);
      });
    }
    this.renderer.listen(this.scrollContainer.nativeElement, 'scroll', () => {
      this.updateCurrentPageNumber();
    });
  }

  private updateCurrentPageNumber(): void {
    const pdfContainer = this.pdfContainer.nativeElement;
    const pages = pdfContainer.getElementsByTagName('div');
    let currentPage = 1;
    let currentVisibleArea = 0;

    for (let i = 0; i < pages.length; i++) {
      const page = pages[i];
      const rect = page.getBoundingClientRect();
      const visibleArea = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);

      if (visibleArea > currentVisibleArea) {
        currentPage = i + 1;
        currentVisibleArea = visibleArea;
      }
    }
    this.currentPageNumber = currentPage;
  }
}
